// code-guardian-ignore-duplicates: drawn class for class as SectionFrontPage on purpose (same Loading skeleton, page width, card grid), so the two read as one screen
// The second step of search, at /search?kind=person&id=<employee code> or
// /search?kind=store&id=<store id>: the name of the person or store that was picked,
// and "Where do you want to look?", one card per page that can show them, each opening
// that page already narrowed.
//
// It is drawn as a section front page is (pages/SectionFrontPage), class for class: the
// same page width, an icon in a filled square beside the title, a plain heading, and a
// grid of white cards, each one link with "Open" at the bottom. Whatever else it has to
// say ("Not found." and the rest) sits in one small white card, centred.
//
// It is an address of its own, so Back returns to it and it can be reopened. The id in
// it can be typed by anyone, so nothing is drawn from the address alone: the server is
// asked, as the person signed in and for the project selected in the top bar, and says
// "not found" the same way for a thing that does not exist and for one this person may
// not see. Switching project asks again; a person or store of the other project is then
// honestly not found.
//
// Only pages the person holds are offered (navigation/whereToLook). Holding a page's
// menu row is not a promise of access: a page with a second permission shows its own
// "access needed" panel, and that is left to the page. Red Flags is the exception: it
// refuses everyone but an admin outright, so its card is offered to admins only.
import { useEffect, useId, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, FolderOpen, SearchX, Store, User, type LucideIcon } from 'lucide-react';
import { Skeleton } from '../components/Skeleton';
import { isLookKind, type LookKind } from '../navigation/searchDestinations';
import { greyLine } from '../navigation/searchGroups';
import { buildSearchIndex } from '../navigation/searchIndex';
import { placesFor, type Place } from '../navigation/whereToLook';
import { searchAPI, type Thing } from '../services/searchApi';
import { useAuthStore } from '../stores/authStore';
import { useCurrentPageStore } from '../stores/currentPageStore';
import { useMenuStore } from '../stores/menuStore';

type Answer =
  | { status: 'ready'; thing: Thing }
  /** No such thing, or not this person's to see: the server does not say which. */
  | { status: 'missing' }
  | { status: 'failed' };

const MISSING: Answer = { status: 'missing' };
const FAILED: Answer = { status: 'failed' };

/** The person or store in an answer, or null when the answer is not one. One with no name is named by its code. */
const readThing = (body: unknown): Thing | null => {
  const answer = body as { success?: unknown; data?: Partial<Thing> | null } | null;
  const data = answer?.data;
  if (answer?.success !== true || !data || typeof data.id !== 'string' || !data.id) return null;
  const code = typeof data.code === 'string' && data.code.trim() ? data.code.trim() : undefined;
  const name = (typeof data.name === 'string' && data.name.trim()) || code;
  if (!name || !isLookKind(data.kind)) return null;
  return { kind: data.kind, id: data.id, name, detail: typeof data.detail === 'string' ? data.detail : '', ...(code ? { code } : {}) };
};

// The section front pages' own measures (pages/SectionFrontPage), so the two read as
// one screen: the page width and margins, the card grid (one card across on a phone,
// then 2, 3 and 4), the white block, and the icon square.
const PAGE = 'mx-auto w-full max-w-[1352px] pb-2 pt-4 sm:px-2';
const CARD_GRID = 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
const FRAME = 'rounded-2xl border border-gray-200 bg-white shadow-sm';
const ICON_SQUARE = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl';

const KIND_ICON: Record<LookKind, LucideIcon> = { person: User, store: Store };

/** The place of the page while it is asked for: the shapes of the title, the heading and a row of cards. */
function Loading(): ReactElement {
  return (
    <div className={PAGE} role="status" aria-label="Loading">
      <div className="flex items-center gap-3">
        <Skeleton className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0 space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
      </div>
      <Skeleton className="mt-9 h-6 w-56 max-w-full" />
      <div className={`mt-4 ${CARD_GRID}`}>
        <Skeleton className="h-44 rounded-2xl" />
        <Skeleton className="h-44 rounded-2xl" />
        <Skeleton className="hidden h-44 rounded-2xl lg:block" />
        <Skeleton className="hidden h-44 rounded-2xl xl:block" />
      </div>
    </div>
  );
}

/** Anything the page says instead of cards: one small white card, centred, an icon over the words. */
function Said({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <div className={`mx-auto mt-9 w-full max-w-md ${FRAME} p-8 text-center`}>
      <span className={`mx-auto ${ICON_SQUARE} bg-primary-50 text-primary-600`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <p className="mt-4 text-sm text-gray-700">{children}</p>
    </div>
  );
}

/** The person's or the store's icon in its filled square, with the name and its grey line beside it. */
function ThingTitle({ kind, thing }: { kind: LookKind; thing: Thing }): ReactElement {
  const Icon = KIND_ICON[kind];
  const grey = greyLine(thing);
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className={`${ICON_SQUARE} bg-primary-600 text-white`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <h1 className="break-words text-3xl font-bold tracking-tight text-gray-900">{thing.name}</h1>
        {grey && <p className="truncate text-sm text-gray-500">{grey}</p>}
      </div>
    </div>
  );
}

/** One place to look: the whole card is the link, and "Open" is part of it, not a second link. */
function PlaceCard({ place }: { place: Place }): ReactElement {
  const Icon = place.icon;
  return (
    <Link
      to={place.to}
      // What the page is for, on hover: it is not printed on the card.
      title={place.hint || undefined}
      className={`flex min-w-0 flex-1 flex-col ${FRAME} p-5 transition hover:border-primary-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
    >
      <span className={`${ICON_SQUARE} bg-primary-50 text-primary-600`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="mt-4 break-words text-[15px] font-semibold text-gray-900">{place.name}</div>
      {/* The period the link opens the page on: the card's one grey line. */}
      <div className="mt-1 text-sm text-gray-500">{place.period}</div>
      {/* mt-auto: at the bottom of the card, so "Open" lines up along a row of cards. */}
      <div className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-primary-600">
        Open
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </div>
    </Link>
  );
}

export default function SearchResultPage(): ReactElement {
  const headingId = useId();
  const [params] = useSearchParams();
  const kind = params.get('kind');
  const id = params.get('id') ?? '';
  const items = useMenuStore((s) => s.items);
  const menuLoaded = useMenuStore((s) => s.loaded);
  const isSuperAdmin = useAuthStore((s) => s.user?.adminRole === 'super_admin');
  // Exactly what the Red Flags page lets people in on (pages/ManagerFocusDebugPage).
  const isAdmin = useAuthStore((s) => s.user?.adminRole === 'super_admin' || s.user?.adminRole === 'admin');
  // No project yet is null, whatever an older saved sign-in left in its place.
  const projectId = useAuthStore((s) => s.selectedProjectId) || null;

  // Everything the answer depends on. Null asks nothing: an address that names no
  // person or store, or no project to look in.
  const lookKind = isLookKind(kind) && id ? kind : null;
  const key = lookKind && projectId !== null ? `${projectId}\n${lookKind}\n${id}` : null;

  // Kept WITH the key it was asked under (as pages/home/useRead keeps a number): the
  // moment the project or the address changes, the old answer stops being shown.
  const [held, setHeld] = useState<{ key: string; answer: Answer } | null>(null);
  const [again, setAgain] = useState(0);

  useEffect(() => {
    if (key === null || lookKind === null || projectId === null) return undefined;
    let current = true;
    const controller = new AbortController();
    Promise.resolve()
      .then(() => searchAPI.thing({ projectId, kind: lookKind, id }, controller.signal))
      .then(
        (res) => {
          if (!current) return;
          const thing = readThing(res.data);
          setHeld({ key, answer: thing ? { status: 'ready', thing } : FAILED });
        },
        (err) => {
          if (!current || controller.signal.aborted) return;
          // 404: not there, or not theirs. 400: the id is not one this kind has.
          const status = (err as { response?: { status?: number } } | null)?.response?.status;
          setHeld({ key, answer: status === 404 || status === 400 ? MISSING : FAILED });
        },
      );
    return () => {
      current = false;
      controller.abort();
    };
  }, [key, lookKind, projectId, id, again]);

  const answer = key !== null && held?.key === key ? held.answer : null;
  const thing = answer?.status === 'ready' ? answer.thing : null;

  // The shell names the trail and the browser tab from this; cleared on the way out.
  const name = thing?.name ?? null;
  useEffect(() => {
    const { setTitle } = useCurrentPageStore.getState();
    setTitle(name);
    return () => setTitle(null);
  }, [name]);

  const index = useMemo(
    () => buildSearchIndex(items, { isSuperAdmin, selectedProjectId: projectId }),
    [items, isSuperAdmin, projectId],
  );
  const places = useMemo(
    () => (thing && lookKind ? placesFor({ kind: lookKind, id: thing.id, code: thing.code }, index, { isAdmin }) : []),
    [thing, lookKind, index, isAdmin],
  );

  if (key === null || lookKind === null || answer?.status === 'missing') {
    return (
      <div className={PAGE}>
        <Said icon={SearchX}>Not found.</Said>
      </div>
    );
  }
  if (answer?.status === 'failed') {
    return (
      <div className={PAGE}>
        <Said icon={AlertTriangle}>
          Could not load this.{' '}
          <button
            type="button"
            onClick={() => { setHeld(null); setAgain((n) => n + 1); }}
            className="font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline"
          >
            Try again
          </button>
        </Said>
      </div>
    );
  }
  // Which places are offered is not known until the menu is: nothing is said before it.
  if (!thing || !menuLoaded) return <Loading />;

  return (
    <div className={PAGE}>
      <ThingTitle kind={lookKind} thing={thing} />
      {places.length === 0 ? (
        <Said icon={FolderOpen}>Nothing to open for this.</Said>
      ) : (
        // A plain heading, then its cards, as on a section front page.
        <section aria-labelledby={headingId} className="mt-9">
          <h2 id={headingId} className="text-lg font-semibold text-gray-900">Where do you want to look?</h2>
          <ul className={`mt-4 ${CARD_GRID}`}>
            {places.map((place) => (
              // flex, with flex-1 on the card: the card fills the height the row was given.
              <li key={place.path} className="flex min-w-0">
                <PlaceCard place={place} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
